import React from 'react';
import { getRepresentatives } from '../../../services/projectRepresentatives';
import { spanneText, tagText, verbinde } from '../handbuchLogic';
import { orgModellLabel } from '../orgModelle';
import type { ProjectGlobalData, ReportMetadata } from '../../../types';
import type { Handbuch } from '../types';
import { A3Seite } from './A3Seite';
import { HANDBUCH_FARBEN } from './stil';

/**
 * Kapitel 1 — Deckblatt.
 *
 * Portiert aus `pages/editor/DeckblattPage.tsx` der Quell-App (70 Z.). Der
 * Aufbau ist derselbe: Titel, Rechtsgrundlage, umrahmter Projektname, darunter
 * die Kenndatentabelle. Getauscht sind ausschliesslich die Datenquellen — die
 * Quell-App las alles aus einem einzigen `Projekt`-Objekt, in OS-IA liegen
 * dieselben Angaben in den Projektstammdaten (`ReportMetadata`), in
 * `globalData` und im Handbuchsatz.
 *
 * ═══ DIESES KAPITEL SCHREIBT NICHTS ════════════════════════════════════════
 * Es liest Projektstammdaten und den Handbuchkopf und fasst beides zusammen.
 * Praezedenzfall im Repo ist die Vorankuendigung (`src/types.ts`: „die
 * Vorankuendigung liest die Projektdaten nur, sie schreibt nie hinein"). Wer
 * hier etwas aendern will, aendert es an der Quelle — sonst stuenden am Ende
 * zwei Bauherren im selben Projekt, und niemand wuesste, welcher gilt.
 *
 * ═══ WARUM FEHLENDE ANGABEN BENANNT WERDEN ═════════════════════════════════
 * Ein leeres Feld auf einem gedruckten Deckblatt liest sich wie eine Aussage:
 * „es gibt keinen Bauherrn". Deshalb steht an jeder Luecke ein Satz, der sagt,
 * WAS fehlt und WO es steht — und am Bildschirm zusaetzlich ein Sprung dorthin.
 * Auf dem Papier faellt der Sprungknopf weg (`print:hidden`), der Satz bleibt.
 */

/** Wohin ein Hinweis springt. Die Werte deutet der Container aus; er darf
 *  einen `(ziel: string) => void`-Handler uebergeben. */
type SprungZiel = 'stammdaten' | 'handbuch' | 'sigeplan';

interface Props {
    /** Projektstammdaten: Name, Nummer, Anschrift, Bauherr, Koordinator. */
    metadata: ReportMetadata;
    /** Bauzeit (`startDate`/`endDate`). */
    globalData?: ProjectGlobalData;
    /** Der Handbuchkopf. `null` = noch keiner angelegt; das Deckblatt zeigt
     *  dann dieselben Projektdaten, nur ohne Fassung und Vergabemodell. */
    handbuch: Handbuch | null;
    /** `sige_projekte.plan_nr` der Planfassung, auf die sich das Handbuch bezieht. */
    planNr?: string;
    /** `sige_projekte.version_label` derselben Planfassung. */
    planVersion?: string;
    /** Anzahl der Gewerkeseiten — kommt aus der geladenen Lage, damit
     *  Deckblatt und Seitenspiegel dieselbe Zahl nennen. */
    anzahlGewerke: number;
    /**
     * Kopfzeile der Seite. Das Deckblatt bekommt ueblicherweise KEINE — es ist
     * selbst der Kopf des Dokuments —, aber die Entscheidung darueber trifft
     * der Container und nicht dieses Blatt.
     */
    kopf?: React.ReactNode;
    /** Fusszeile der Seite, ueblicherweise ein `<SeitenFuss …/>`. */
    fuss?: React.ReactNode;
    onSprung?: (ziel: SprungZiel) => void;
}

interface Kenndatenzeile {
    bezeichnung: string;
    /** Der Wert. Leer heisst: die Quelle gibt nichts her. */
    wert: string;
    /** Was dann statt des Wertes dasteht — ein Satz, keine Leere. */
    fehlt?: string;
    ziel?: SprungZiel;
}

/**
 * Die Kenndatentabelle des Deckblatts.
 *
 * Baut Werte UND Fehlmeldungen an einer Stelle, damit die Tabelle im JSX nur
 * noch ausgegeben werden muss. Die Reihenfolge folgt der Quell-App (Bauherr,
 * Ansprechpartner, Vergabemodell, SiGeKo, Bauzeitraum, Gewerke, Fassung) und
 * ist um die Angaben ergaenzt, die es dort nicht gab: Gueltigkeit, Planbezug,
 * Katalogfassung und Status.
 */
const kenndaten = (
    metadata: ReportMetadata,
    globalData: ProjectGlobalData | undefined,
    handbuch: Handbuch | null,
    planNr: string | undefined,
    planVersion: string | undefined,
    anzahlGewerke: number,
): Kenndatenzeile[] => {
    // Quelle der Wahrheit fuer den Koordinator ist die Vertreterliste; die
    // Legacy-Felder `coordinator*` sind nur ihr Spiegel. `getRepresentatives`
    // leitet die Liste bei Altprojekten aus genau diesen Feldern ab, deshalb
    // deckt der eine Aufruf beide Faelle ab.
    const sigeko = getRepresentatives(metadata)[0];
    const sigekoText = verbinde([
        sigeko?.name || metadata.coordinator,
        sigeko?.phone || metadata.coordinatorPhone,
    ]);

    const freigabe = handbuch?.status === 'freigegeben'
        ? verbinde([
            `freigegeben am ${tagText(handbuch.freigegeben_am)}`,
            handbuch.freigegeben_von_name,
        ], ' durch ')
        : 'Entwurf — noch nicht freigegeben';

    return [
        {
            bezeichnung: 'Bauherr',
            wert: (metadata.clientName || '').trim(),
            fehlt: 'Bauherr in den Projektdaten nicht hinterlegt',
            ziel: 'stammdaten',
        },
        {
            bezeichnung: 'Anschrift Bauherr',
            wert: (metadata.clientAddress || '').trim(),
            fehlt: 'Anschrift des Bauherrn nicht hinterlegt',
            ziel: 'stammdaten',
        },
        {
            bezeichnung: 'Ansprechpartner',
            wert: verbinde([metadata.clientContactPerson, metadata.clientPhone, metadata.clientEmail]),
            fehlt: 'Ansprechpartner des Bauherrn nicht hinterlegt',
            ziel: 'stammdaten',
        },
        {
            bezeichnung: 'Vergabemodell',
            // Ohne Modell bleibt das Organigramm leer — das ist die Folge, und
            // sie steht hier, damit sie nicht erst zwei Seiten spaeter auffaellt.
            wert: handbuch?.org_modell ? orgModellLabel(handbuch.org_modell) : '',
            fehlt: 'Vergabemodell noch nicht bestimmt — ohne es bleibt das Organigramm leer',
            ziel: 'handbuch',
        },
        {
            bezeichnung: 'SiGeKo',
            wert: sigekoText,
            fehlt: 'Kein Koordinator im Projekt hinterlegt',
            ziel: 'stammdaten',
        },
        {
            bezeichnung: 'Bauzeitraum',
            wert: spanneText(globalData?.startDate, globalData?.endDate),
            fehlt: 'Bauzeit im Projekt nicht hinterlegt',
            ziel: 'stammdaten',
        },
        {
            bezeichnung: 'Gültigkeit',
            wert: spanneText(handbuch?.gueltig_von, handbuch?.gueltig_bis),
            fehlt: 'Gültigkeitszeitraum des Handbuchs noch nicht gesetzt',
            ziel: 'handbuch',
        },
        {
            bezeichnung: 'Gewerkeseiten',
            wert: anzahlGewerke > 0 ? String(anzahlGewerke) : '',
            fehlt: 'Noch keine Gewerkeseite angelegt',
            ziel: 'handbuch',
        },
        {
            bezeichnung: 'Planbezug',
            wert: verbinde([planNr && `Plan-Nr. ${planNr}`, planVersion], ' · '),
            fehlt: 'Kein SiGe-Plan verknüpft — Gefährdungsmatrix und Zeitplan bleiben leer',
            ziel: 'sigeplan',
        },
        {
            bezeichnung: 'Fassung / Stand',
            wert: verbinde([handbuch?.version_label, handbuch?.stand && `Stand ${handbuch.stand}`]),
            fehlt: 'Noch keine Fassung vergeben',
            ziel: 'handbuch',
        },
        {
            bezeichnung: 'Katalogfassung',
            // Kein `fehlt`: die Spalte ist `not null`. Steht hier nichts, ist
            // der Satz aelter als die Migration — dann ist die leere Zelle die
            // ehrlichere Auskunft als ein erfundener Wert.
            wert: (handbuch?.katalog_version || '').trim(),
        },
        {
            bezeichnung: 'Status',
            wert: freigabe,
        },
    ];
};

// ═══════════════════════════════════════════════════════════════════════════
// BAUSTEINE
// ═══════════════════════════════════════════════════════════════════════════

/** Eine benannte Luecke: der Satz bleibt im Druck stehen, der Sprungknopf
 *  nicht — auf Papier kann man nirgendwohin springen. */
const Luecke: React.FC<{
    text: string;
    ziel?: SprungZiel;
    onSprung?: (ziel: SprungZiel) => void;
}> = ({ text, ziel, onSprung }) => (
    <span style={{ color: HANDBUCH_FARBEN.statusFehlt, fontStyle: 'italic' }}>
        {text}
        {ziel && onSprung && (
            <button
                type="button"
                onClick={() => onSprung(ziel)}
                className="print:hidden ml-2 underline"
                style={{ fontStyle: 'normal', fontSize: '8px', color: HANDBUCH_FARBEN.textLeise }}
            >
                → hinterlegen
            </button>
        )}
    </span>
);

export const Deckblatt: React.FC<Props> = ({
    metadata, globalData, handbuch, planNr, planVersion, anzahlGewerke,
    kopf, fuss, onSprung,
}) => {
    const zeilen = kenndaten(metadata, globalData, handbuch, planNr, planVersion, anzahlGewerke);
    const anschrift = (metadata.projectAddress || '').trim();
    const nummer = (metadata.projectNumber || '').trim();

    return (
        /* Das Deckblatt traegt ueblicherweise KEINE Kopfzeile: es ist selbst der
           Kopf des Dokuments. Genau dafuer ist `kopf` optional. */
        <A3Seite kopf={kopf} fuss={fuss}>
            <div className="flex-1 flex flex-col items-center justify-center text-center">
                <div style={{ fontSize: '24px', fontWeight: 700, letterSpacing: '0.02em', marginBottom: '6px' }}>
                    SICHERHEITS- UND GESUNDHEITSSCHUTZ-HANDBUCH
                </div>
                <div style={{ fontSize: '12px', color: HANDBUCH_FARBEN.textLeise, marginBottom: '26px' }}>
                    gemäß Baustellenverordnung (BaustellV) / RAB 31
                </div>

                <div
                    className="w-full"
                    style={{
                        border: `2px solid ${HANDBUCH_FARBEN.linieStark}`,
                        padding: '22px',
                        maxWidth: '520px',
                        marginBottom: '26px',
                    }}
                >
                    <div style={{ fontSize: '18px', fontWeight: 700 }}>
                        {metadata.projectName || (
                            <Luecke
                                text="Projektname nicht hinterlegt"
                                ziel="stammdaten"
                                onSprung={onSprung}
                            />
                        )}
                    </div>
                    <div style={{ fontSize: '11px', color: HANDBUCH_FARBEN.textLeise, marginTop: '8px' }}>
                        {anschrift || (
                            <Luecke
                                text="Baustellenanschrift nicht hinterlegt"
                                ziel="stammdaten"
                                onSprung={onSprung}
                            />
                        )}
                    </div>
                    {nummer && (
                        <div style={{ fontSize: '10px', color: HANDBUCH_FARBEN.textLeise, marginTop: '3px' }}>
                            Projekt-Nr. {nummer}
                        </div>
                    )}
                </div>

                <table style={{ width: '460px', fontSize: '10px', textAlign: 'left', borderCollapse: 'collapse' }}>
                    <tbody>
                        {zeilen.map((z, i) => (
                            <tr key={z.bezeichnung} style={i === 0 ? undefined : { borderTop: '1px solid #dddddd' }}>
                                <td
                                    style={{
                                        padding: '5px 12px 5px 0',
                                        width: '150px',
                                        verticalAlign: 'top',
                                        color: HANDBUCH_FARBEN.textLeise,
                                    }}
                                >
                                    {z.bezeichnung}
                                </td>
                                <td style={{ padding: '5px 0', verticalAlign: 'top' }}>
                                    {z.wert
                                        ? <span style={{ fontWeight: 600 }}>{z.wert}</span>
                                        : z.fehlt
                                            ? <Luecke text={z.fehlt} ziel={z.ziel} onSprung={onSprung} />
                                            : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </A3Seite>
    );
};
